import type { ApiClient } from "@/modules/api/apiClient.ts";
import { getResponseData } from "@/modules/api/apiClient.ts";
import { toastOptions } from "@/modules/compute/vmQuery.ts";
import { raiseRequestToast } from "@/modules/notifications/toast.tsx";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import { queryOptions, type QueryClient, type QueryKey } from "@tanstack/react-query";
import type { SshKey, SshKeyCreateBody, SshKeyUpdateBody } from "@warrenio/api-spec/spec.oats.gen";

type Response = SshKey[];

const queryKey: QueryKey = ["user-resource/ssh_keys"];

export function sshKeysQuery(client: ApiClient) {
    return queryOptions({
        queryKey,
        queryFn: async ({ signal }): Promise<Response> => {
            return getResponseData(await client.GET("/user-resource/ssh_keys", { signal }));
        },
    });
}

export function addSshKeyMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["user-resource/add_ssh_key"],
        async mutationFn({ body }: { body: SshKeyCreateBody }) {
            return getResponseData(
                await api.POST("/user-resource/ssh_keys", {
                    ...jsonEncodedBody,
                    body,
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "SSH key created",
                error: "Error creating SSH key",
            });
            await queryClient.invalidateQueries({ queryKey });
        },
    });
}

export function updateSshKeyMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["user-resource/update_ssh_key"],
        async mutationFn({ uuid, body }: { uuid: string; body: SshKeyUpdateBody }) {
            return getResponseData(
                await api.PATCH("/user-resource/ssh_keys/{uuid}", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid } },
                }),
            );
        },
        onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "SSH key updated",
                error: "Error updating SSH key",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey }));
        },
    });
}

export function deleteSshKeyMutation(api: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["user-resource/delete_ssh_key"],
        async mutationFn({ uuid }: { uuid: string }) {
            return getResponseData(
                await api.DELETE("/user-resource/ssh_keys/{uuid}", {
                    params: { path: { uuid } },
                }),
            );
        },
        onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "SSH key deleted",
                error: "Error deleting SSH key",
            });
            optimisticPromise(queryClient.invalidateQueries({ queryKey }));
        },
    });
}
